import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, brandStory, testimonials, faqs } from '@/mocks/site';
import { useState } from 'react';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    foundingDate: siteInfo.foundingYear,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteInfo.phone,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`关于我们 | 品牌介绍与联系方式 - ${siteInfo.name}`}
        description={`了解${siteInfo.name}的品牌故事、发展历程和联系方式。${siteInfo.name}汇聚日本原装正品，严选品质保障，值得信赖的日本精品购物平台。`}
        keywords={`关于我们,品牌介绍,联系方式,${siteInfo.keywords}`}
        canonicalUrl={`${siteInfo.url}/about`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="pt-20 pb-4 bg-background-100">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Brand Story Section */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            <div className="lg:w-1/2">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                {brandStory.title}
              </h1>
              {brandStory.paragraphs.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {p}
                </p>
              ))}
            </div>
            <div className="lg:w-1/2">
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20Japanese%20storefront%20with%20warm%20lighting%20and%20minimal%20design%2C%20premium%20products%20displayed%20beautifully%2C%20cream%20and%20wood%20tones%2C%20zen%20atmosphere%2C%20editorial%20photography%2C%20sophisticated%20retail%20aesthetic&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt={`${siteInfo.name}品牌形象`}
                  title={`${siteInfo.name} - 品牌故事`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { value: '200+', label: '合作品牌' },
                { value: '5,000+', label: '精选商品' },
                { value: '50,000+', label: '满意客户' },
                { value: '99.8%', label: '好评率' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-primary-600 font-heading">{stat.value}</p>
                  <p className="text-sm text-foreground-500 mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((item) => (
                <div key={item.id} className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{item.content}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span className="font-medium text-foreground-800">{item.name}</span>
                    <span>{item.location} · {item.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors hover:bg-background-200/50"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    {openFaq === i ? (
                      <i className="ri-arrow-up-s-line text-foreground-500 transition-transform flex-shrink-0"></i>
                    ) : (
                      <i className="ri-arrow-down-s-line text-foreground-500 transition-transform flex-shrink-0"></i>
                    )}
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-phone-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-medium text-foreground-900 mb-2">客服电话</h3>
                <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                <p className="text-xs text-foreground-400 mt-1">{siteInfo.businessHours}</p>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-medium text-foreground-900 mb-2">电子邮箱</h3>
                <p className="text-sm text-foreground-600">{siteInfo.email}</p>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-map-pin-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-medium text-foreground-900 mb-2">公司地址</h3>
                <p className="text-sm text-foreground-600">{siteInfo.address}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}